import { GRAPHICS_PRESETS, graphicsPreset, graphicsSchema, saveGraphicsSettings, type GraphicsPreset, type GraphicsSettings } from './graphics-settings';
import type { Viewport } from './viewport';

const el = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

export class GraphicsPanel {
  private events = new AbortController();
  constructor(private viewport: Viewport, private stopInput: () => void) {
    const options = { signal: this.events.signal };
    el<HTMLSelectElement>('graphics-preset').addEventListener('change', event => {
      const preset = (event.target as HTMLSelectElement).value;
      if (preset !== 'custom') {
        const {frameLimit,targetFPS,minimumScale}=this.viewport.graphicsSettings;
        this.apply({ ...GRAPHICS_PRESETS[preset as GraphicsPreset],frameLimit,targetFPS,minimumScale });
      }
    }, options);
    for (const control of document.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-graphics-setting]')) control.addEventListener(control instanceof HTMLInputElement ? 'input' : 'change', () => {
      const settings = { ...this.viewport.graphicsSettings, [control.dataset.graphicsSetting!]: control instanceof HTMLInputElement && control.type === 'checkbox' ? control.checked : control.dataset.numeric !== undefined ? Number(control.value) : control.value };
      this.apply(graphicsSchema.parse(settings));
    }, options);
    el('graphics-reset').addEventListener('click', () => this.apply({ ...GRAPHICS_PRESETS.medium }), options);
    this.render();
  }
  private apply(settings: GraphicsSettings): void {
    settings.minimumScale=Math.min(settings.minimumScale,settings.renderScale);
    this.stopInput(); this.viewport.applyGraphicsSettings(settings);
    el('graphics-status').textContent = saveGraphicsSettings(settings) ? 'Applied and saved in this browser.' : 'Applied for this session; browser storage is unavailable.';
    this.render();
  }
  private render(): void {
    const settings = this.viewport.graphicsSettings;
    el<HTMLSelectElement>('graphics-preset').value = graphicsPreset(settings);
    for (const control of document.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-graphics-setting]')) {
      if (control instanceof HTMLInputElement && control.type === 'checkbox') control.checked = Boolean(settings[control.dataset.graphicsSetting as keyof GraphicsSettings]);
      else control.value = String(settings[control.dataset.graphicsSetting as keyof GraphicsSettings]);
    }
    el<HTMLOutputElement>('graphics-scale-value').value = `${Math.round(settings.renderScale * 100)}%`;
    el<HTMLOutputElement>('graphics-ao-value').value = `${Math.round(settings.aoStrength*100)}%`;
    el<HTMLOutputElement>('graphics-minimum-value').value = `${Math.round(settings.minimumScale*100)}%`;
    this.refresh();
  }
  refresh(): void {
    const { effective, resolution, capabilities } = this.viewport.diagnostics.graphics;
    const budget=this.viewport.frameBudget.diagnostics;
    const probes=this.viewport.diagnostics.lighting.probes;
    el('graphics-effective').textContent = `${resolution[0]} × ${resolution[1]} render pixels · ${effective.textureFiltering}× filtering${effective.samples ? ` · ${effective.samples}× MSAA` : ''}${budget.targetFPS?` · Auto ${Math.round(budget.renderScale*100)}%${budget.calibrating?' (measuring refresh)':` / ${Math.round(budget.effectiveTargetFPS)} FPS target`}`:''}${!capabilities.hdr ? ' · Basic lighting; AO/AA unavailable; global reflections' : ''}${this.viewport.graphicsSettings.antialiasing === 'msaa' && effective.antialiasing === 'fxaa' ? ' · FXAA fallback' : ''}`;
    el('graphics-probes').textContent=!probes.authored?'Global lighting · no room probes':`${probes.resident} resident local probes · ${probes.baked} matching bakes${probes.pending?' · capturing…':''}${probes.error?' · capture unavailable; using global fallback':''}`;
  }
  dispose(): void { this.events.abort(); }
}
